/**
 * Create or edit a custom case type: its name, the case type it inherits
 * from, and (optionally) its own Draft Letters list with each letter's
 * template. Forms follow the base type; the backend stores `forms` but this
 * dialog doesn't edit them yet.
 */
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import {
  RETIRED_VISAS,
  RFE_VISA_CATEGORY_ID
} from "@/components/common/visaOptions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { draftTypeLabel } from "@/features/caseIde/workflows/defaultTemplates";
import { VisaPicker } from "@/features/clientProfile/components/VisaPicker";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";
import { VISAVALUE } from "@/types/visa.type";

import { customCaseTypesApi } from "../api";
import {
  LETTER_DRAFT_TYPES,
  MAX_TYPE_NAME,
  cleanTypeName,
  customTypeLabel,
  templateChoicesFor
} from "../customCaseTypes";
import type {
  CustomCaseType,
  CustomCaseTypeInput,
  CustomCaseTypeLetter
} from "../types";

/** The Select value for CaseBlink's standard template (Radix refuses ""). */
const STANDARD = "__caseblink__";

type Props = {
  lawFirmId: string;
  /** The type to edit; null creates one. Undefined keeps the dialog closed. */
  editing: CustomCaseType | null | undefined;
  library: readonly FirmLibraryEntry[];
  onOpenChange: (open: boolean) => void;
  onSaved: (type: CustomCaseType) => void;
};

type LetterRows = Record<string, CustomCaseTypeLetter | undefined>;

const rowsOf = (letters: CustomCaseTypeLetter[] | null): LetterRows =>
  Object.fromEntries((letters ?? []).map((l) => [l.draft_type, l]));

type LetterRowProps = {
  draftType: string;
  row: CustomCaseTypeLetter | undefined;
  library: readonly FirmLibraryEntry[];
  onChange: (row: CustomCaseTypeLetter | undefined) => void;
};

const LetterRow: FC<LetterRowProps> = ({
  draftType,
  row,
  library,
  onChange
}) => {
  const id = `custom-case-type-letter-${draftType}`;
  const templates = templateChoicesFor(library, draftType);
  return (
    <li
      className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-1 hover:bg-fill-1"
      data-testid={id}
    >
      <Checkbox
        id={id}
        checked={Boolean(row)}
        onCheckedChange={(on) =>
          onChange(
            on === true
              ? {
                  draft_type: draftType,
                  checked: true,
                  template_entry_id: null
                }
              : undefined
          )
        }
      />
      <Label htmlFor={id} className="min-w-0 flex-1 font-normal">
        {draftTypeLabel(draftType)}
      </Label>
      {row ? (
        <>
          <span className="flex items-center gap-1.5">
            <Checkbox
              id={`${id}-checked`}
              checked={row.checked}
              onCheckedChange={(on) =>
                onChange({ ...row, checked: on === true })
              }
            />
            <Label
              htmlFor={`${id}-checked`}
              className="text-sm font-normal text-fg-3"
            >
              On by Default
            </Label>
          </span>
          <Select
            value={row.template_entry_id ?? STANDARD}
            onValueChange={(v) =>
              onChange({
                ...row,
                template_entry_id: v === STANDARD ? null : v
              })
            }
          >
            <SelectTrigger
              size="sm"
              className="w-48"
              aria-label={`${draftTypeLabel(draftType)} Template`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={STANDARD}>CaseBlink Standard</SelectItem>
              {templates.map((t) => (
                <SelectItem key={t.entryId} value={t.entryId}>
                  {t.name}
                </SelectItem>
              ))}
              {row.template_entry_id &&
              !templates.some((t) => t.entryId === row.template_entry_id) ? (
                <SelectItem value={row.template_entry_id} disabled>
                  Removed Template
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
        </>
      ) : null}
    </li>
  );
};

export const CustomCaseTypeDialog: FC<Props> = ({
  lawFirmId,
  editing,
  library,
  onOpenChange,
  onSaved
}) => {
  const open = editing !== undefined;
  const [name, setName] = useState("");
  const [baseVisa, setBaseVisa] = useState<string | null>(null);
  const [ownLetters, setOwnLetters] = useState(false);
  const [rows, setRows] = useState<LetterRows>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setBaseVisa(editing?.base_visa ?? null);
    setOwnLetters(Boolean(editing?.letters));
    setRows(rowsOf(editing?.letters ?? null));
  }, [open, editing]);

  const cleanName = cleanTypeName(name);
  const letters = useMemo(
    () =>
      LETTER_DRAFT_TYPES.flatMap((t) => {
        const row = rows[t];
        return row ? [row] : [];
      }),
    [rows]
  );
  const invalid =
    !cleanName ||
    cleanName.length > MAX_TYPE_NAME ||
    (ownLetters && letters.length === 0);

  const setRow = (draftType: string, row: CustomCaseTypeLetter | undefined) =>
    setRows((cur) => ({ ...cur, [draftType]: row }));

  const save = async () => {
    if (invalid) return;
    const input: CustomCaseTypeInput = {
      name: cleanName,
      base_visa: baseVisa,
      letters: ownLetters ? letters : null
    };
    setSaving(true);
    try {
      const saved = editing
        ? await customCaseTypesApi.update(lawFirmId, editing.id, input)
        : await customCaseTypesApi.create(lawFirmId, input);
      toast.success(
        editing
          ? `Saved ${customTypeLabel(saved.name)}`
          : `Added ${customTypeLabel(saved.name)}`
      );
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Couldn't save the case type."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-xl"
        data-testid="custom-case-type-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Custom Case Type" : "New Custom Case Type"}
          </DialogTitle>
          <DialogDescription>
            Attorneys pick it on New Case as &quot;
            {customTypeLabel(cleanName || "Name")}&quot;. Its cases start with
            these letters and templates.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="custom-case-type-name">Name</Label>
            <Input
              id="custom-case-type-name"
              value={name}
              maxLength={MAX_TYPE_NAME + 8}
              placeholder="EB-1A + Consular"
              onChange={(e) => setName(e.target.value)}
              data-testid="custom-case-type-name"
            />
            {cleanName.length > MAX_TYPE_NAME ? (
              <span className="text-sm text-danger">
                Use {MAX_TYPE_NAME} characters or fewer.
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="custom-case-type-base">Based On</Label>
            <div className="flex items-center gap-2">
              <VisaPicker
                id="custom-case-type-base"
                value={baseVisa}
                onChange={setBaseVisa}
                placeholder="Nothing (Plain Custom Workflow)"
                excludeCategories={[RFE_VISA_CATEGORY_ID]}
                excludeVisas={[VISAVALUE.CUSTOM_TEMPLATE, ...RETIRED_VISAS]}
                className="min-w-0 flex-1"
              />
              {baseVisa ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBaseVisa(null)}
                >
                  Clear
                </Button>
              ) : null}
            </div>
            <span className="text-sm text-fg-3">
              Its letters, forms and templates are the starting point.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="custom-case-type-own-letters">
                Choose Its Own Letters
              </Label>
              <Switch
                id="custom-case-type-own-letters"
                checked={ownLetters}
                onCheckedChange={setOwnLetters}
                data-testid="custom-case-type-own-letters"
              />
            </div>
            {!ownLetters ? (
              <span className="text-sm text-fg-3">
                {baseVisa
                  ? `Draft Letters lists the ${baseVisa} letters.`
                  : "Draft Letters lists Custom Workflow's letters."}
              </span>
            ) : (
              <ul
                className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-lg p-1 ring-1 ring-line-1"
                data-testid="custom-case-type-letters"
              >
                {LETTER_DRAFT_TYPES.map((draftType) => (
                  <LetterRow
                    key={draftType}
                    draftType={draftType}
                    row={rows[draftType]}
                    library={library}
                    onChange={(row) => setRow(draftType, row)}
                  />
                ))}
              </ul>
            )}
            {ownLetters && letters.length === 0 ? (
              <span className="text-sm text-fg-3">
                Pick at least one letter.
              </span>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={invalid || saving}
            onClick={() => void save()}
            data-testid="custom-case-type-save"
          >
            {editing ? "Save" : "Add Case Type"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
